import {
  Headphones,
  LibraryBooks as LibraryBooksIcon,
  PictureAsPdf,
  PlayCircleOutline,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatBottomNav from '../components/chat/ChatBottomNav';
import FeaturedCarousel from '../components/content/FeaturedCarousel';
import AppHeader from '../components/layout/AppHeader';
import API_ENDPOINTS from '../config/api';
import { useAuth } from '../contexts/AuthContext';

// Types
interface ContentCategory {
  id: string;
  name: string;
  slug: string;
  is_provider_tag?: boolean;
}

interface ContentItem {
  id: string;
  title: string;
  description: string;
  content_type: 'video' | 'audio' | 'pdf' | 'link';
  thumbnail_url: string;
  external_url: string;
  duration_seconds: number | null;
  categories: ContentCategory[];
  published_at: string | null;
  is_pinned: boolean;
  is_new: boolean;
  is_featured: boolean;
}

// Reason: map content type to appropriate MUI icon
function ContentTypeIcon({ type }: { type: string }): React.ReactElement {
  switch (type) {
    case 'video':
      return <PlayCircleOutline sx={{ color: '#FF0000', fontSize: 20 }} />;
    case 'audio':
      return <Headphones sx={{ color: '#1DB954', fontSize: 20 }} />;
    case 'pdf':
      return <PictureAsPdf sx={{ color: '#E53935', fontSize: 20 }} />;
    default:
      return <LibraryBooksIcon sx={{ color: '#666', fontSize: 20 }} />;
  }
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

const ContentPage: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState<ContentItem[]>([]);
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState('');

  // Reason: show carousel on landing, list when user searches or filters
  const isBrowsing = !!(search.trim() || categoryFilter);
  const featuredItems = items.filter((i) => i.is_featured);

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const loadCategories = useCallback(async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.CONTENT.CATEGORIES, { headers });
      setCategories(res.data || []);
    } catch {
      // Non-critical — categories dropdown just won't populate
    }
  }, [token]);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      if (categoryFilter) params.append('category', categoryFilter);

      const url = `${API_ENDPOINTS.CONTENT.ITEMS}?${params.toString()}`;
      const res = await axios.get(url, { headers });
      setItems(res.data.results || []);
    } catch {
      setError('Failed to load content library');
    } finally {
      setLoading(false);
    }
  }, [token, search, categoryFilter]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    const debounce = setTimeout(() => loadItems(), 300);
    return () => clearTimeout(debounce);
  }, [loadItems]);

  const handleToggleSelect = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = (): void => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  };

  const handleBulkAction = async (action: string): Promise<void> => {
    setBulkAction(action);
    if (action === 'pin' && selectedIds.size > 0) {
      try {
        const allPinned = Array.from(selectedIds).every(
          (id) => items.find((i) => i.id === id)?.is_pinned
        );
        await Promise.all(
          Array.from(selectedIds).map((id) => {
            const url = allPinned
              ? API_ENDPOINTS.CONTENT.UNPIN(id)
              : API_ENDPOINTS.CONTENT.PIN(id);
            return axios.post(url, {}, { headers });
          })
        );
        // Refresh list to reflect pin changes
        await loadItems();
        setSelectedIds(new Set());
      } catch {
        setError('Failed to pin/unpin selected content');
      }
    }
    setBulkAction('');
  };

  return (
    <Box
      sx={{
        height: 'var(--app-height, 100vh)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
      }}
    >
      <AppHeader
        title="Content"
        leftContent={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={items.length > 0 && selectedIds.size === items.length}
              indeterminate={selectedIds.size > 0 && selectedIds.size < items.length}
              onChange={handleSelectAll}
              sx={{ '& .MuiSvgIcon-root': { color: '#FFFFFF' } }}
            />
            <Typography
              variant="body2"
              sx={{ color: '#828282', fontWeight: 600, mr: 0.5 }}
            >
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
            </Typography>
            {selectedIds.size > 0 && (
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={bulkAction}
                  displayEmpty
                  onChange={(e) => handleBulkAction(e.target.value)}
                  sx={{
                    borderRadius: '10px',
                    fontSize: 13,
                    height: 32,
                    bgcolor: '#FFFFFF',
                    '& .MuiSelect-select': { py: 0.5 },
                  }}
                >
                  <MenuItem value="" disabled>Actions</MenuItem>
                  <MenuItem value="pin">
                    {Array.from(selectedIds).every(
                      (id) => items.find((i) => i.id === id)?.is_pinned
                    ) ? 'Unpin' : 'Pin'}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        }
      />

      {/* Search and filter bar */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Search content..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LibraryBooksIcon sx={{ color: '#AFAFAF', fontSize: 20 }} />
              </InputAdornment>
            ),
            sx: { borderRadius: '20px', fontSize: 14 },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={categoryFilter}
            displayEmpty
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{ borderRadius: '20px', fontSize: 14 }}
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.slug}>{cat.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Main content area */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : isBrowsing ? (
          /* ── Filtered list (search or category active) ── */
          items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <LibraryBooksIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No matching content
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search or filters.
              </Typography>
            </Box>
          ) : (
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 0, pt: 1 }}>
              {items.map((item) => {
                const isSelected = selectedIds.has(item.id);
                const isProviderContent = item.categories.some((c) => c.is_provider_tag);
                return (
                  <ListItem key={item.id} disableGutters sx={{ p: 0 }}>
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* Checkbox */}
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggleSelect(item.id)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ flexShrink: 0, '& .MuiSvgIcon-root': { color: '#AFAFAF' } }}
                      />

                      {/* Content row — clickable */}
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/content/${item.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/content/${item.id}`);
                          }
                        }}
                        sx={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          bgcolor: isSelected ? '#D0D0D0' : '#E8E8E8',
                          borderRadius: '20px',
                          border: isProviderContent ? '2px solid #2F80B7' : '2px solid transparent',
                          px: 1.5,
                          py: 1.25,
                          cursor: 'pointer',
                          outline: 'none',
                          filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.1))',
                          '&:hover': { bgcolor: 'rgba(255, 212, 0, 0.08)' },
                        }}
                      >
                        {/* Thumbnail */}
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: '12px',
                            overflow: 'hidden',
                            flexShrink: 0,
                            bgcolor: '#D0D0D0',
                          }}
                        >
                          {item.thumbnail_url ? (
                            <img
                              src={item.thumbnail_url}
                              alt={item.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <ContentTypeIcon type={item.content_type} />
                            </Box>
                          )}
                        </Box>

                        {/* Title + meta */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <ContentTypeIcon type={item.content_type} />
                            {item.is_new && (
                              <Chip
                                label="NEW!"
                                size="small"
                                sx={{
                                  height: 18,
                                  bgcolor: '#FFD400',
                                  color: '#333',
                                  fontWeight: 700,
                                  '& .MuiChip-label': { px: 0.75, fontSize: 10 },
                                }}
                              />
                            )}
                            {item.duration_seconds && (
                              <Typography sx={{ fontSize: 11, color: '#828282' }}>
                                {formatDuration(item.duration_seconds)}
                              </Typography>
                            )}
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: 'Inter, system-ui, sans-serif',
                              fontSize: 13,
                              fontWeight: item.is_pinned ? 700 : 400,
                              color: '#333',
                              lineHeight: 1.4,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {item.title}
                          </Typography>
                          {item.categories.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                              {item.categories.map((cat) => (
                                <Chip
                                  key={cat.id}
                                  label={cat.name}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    bgcolor: isProviderContent ? '#2F80B7' : '#F0F0F0',
                                    color: isProviderContent ? '#FFFFFF' : '#666',
                                    '& .MuiChip-label': { px: 0.75, fontSize: 10 },
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>

                        {/* Pin indicator */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const url = item.is_pinned
                              ? API_ENDPOINTS.CONTENT.UNPIN(item.id)
                              : API_ENDPOINTS.CONTENT.PIN(item.id);
                            axios.post(url, {}, { headers }).then(() => loadItems());
                          }}
                          sx={{ flexShrink: 0 }}
                        >
                          {item.is_pinned
                            ? <Star sx={{ color: '#FFD400', fontSize: 22 }} />
                            : <StarBorder sx={{ color: '#AFAFAF', fontSize: 22 }} />}
                        </IconButton>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )
        ) : (
          /* ── Featured carousel (landing view) ── */
          <FeaturedCarousel
            items={featuredItems}
            onItemClick={(id) => navigate(`/content/${id}`)}
          />
        )}
      </Box>

      <ChatBottomNav />
    </Box>
  );
};

export default ContentPage;
